import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Plus, Fuel, Eye, Trash2, Calendar,
  ChevronDown, ChevronRight, FolderOpen, Folder, Archive, Zap,
  Search, RefreshCw, XCircle, CreditCard
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../api.js';
import { useActiveVehicles, useActiveDrivers } from '../../hooks/useDataHooks.js';
import { useAccordionState } from '../../hooks/useAccordionState.js';
import FuelFormModal from './FuelFormModal.jsx';
import FuelDetailModal from './FuelDetailModal.jsx';
import FuelCardView from './FuelCardView.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import { useProfile } from '../../v3/context/ProfileContext.jsx';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../../i18n/format.js';

// Loading Spinner
function LoadingSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center items-center h-64">
      <div className="text-gray-600">{t('common.states.loading')}</div>
    </div>
  );
}

// Fuel type configuration — le libellé vient de i18n (fuel.types.<code>)
const FUEL_TYPE_CONFIG = {
  gasoline: { color: 'bg-green-100 text-green-800', icon: '⛽' },
  diesel: { color: 'bg-yellow-100 text-yellow-800', icon: '🛢️' },
  electric: { color: 'bg-blue-100 text-blue-800', icon: '⚡' },
  hybrid: { color: 'bg-purple-100 text-purple-800', icon: '🔋' },
  adblue: { color: 'bg-cyan-100 text-cyan-800', icon: '💧' },
  engine_oil: { color: 'bg-amber-100 text-amber-800', icon: '🛢️' },
  coolant: { color: 'bg-teal-100 text-teal-800', icon: '❄️' },
  washer_fluid: { color: 'bg-sky-100 text-sky-800', icon: '💦' },
  other: { color: 'bg-gray-100 text-gray-800', icon: '🔧' }
};

function FuelView({ onMessage, createForVehicleId, defaultFuelType, returnTo, onNavigate }) {
  const { t, fmt } = useFormat();
  const queryClient = useQueryClient();
  const { isTabVisible, isReadOnly } = useProfile();
  const [mainTab, setMainTab] = useState('logs'); // logs | cards
  const [fuelLoading, setFuelLoading] = useState(true);
  const [fuelLogs, setFuelLogs] = useState([]);
  const { data: vehicles = [], isLoading: vehiclesLoading } = useActiveVehicles();
  const { data: drivers = [], isLoading: driversLoading } = useActiveDrivers();
  const [showForm, setShowForm] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const { searchQuery } = useSearch();
  // Devise : celle de l'org du plein ; totaux convertis dans la devise du périmètre.
  const { money, moneyScope, sumScope, toScope, currencyOf, symbolOf, scopeSymbol } = useOrgCurrency();
  const createForVehicleProcessed = useRef(false);

  const showLogsTab = isTabVisible('fuel', 'logs');
  const showCardsTab = isTabVisible('fuel', 'cards');

  // Reset to first visible tab if current is hidden
  useEffect(() => {
    if (mainTab === 'logs' && !showLogsTab && showCardsTab) setMainTab('cards');
    if (mainTab === 'cards' && !showCardsTab && showLogsTab) setMainTab('logs');
  }, [showLogsTab, showCardsTab, mainTab]);

  // Accordion state - persisted to DB
  const {
    isExpanded,
    toggleGroup: toggleAccordion,
  } = useAccordionState({ viewCode: 'fuel' });

  // Toggle wrappers with prefixed keys
  const toggleGroup = (group) => toggleAccordion(`group:${group}`);
  const toggleMonth = (monthKey) => toggleAccordion(`month:${monthKey}`);

  const loading = fuelLoading || vehiclesLoading || driversLoading;

  const loadData = async () => {
    try {
      setFuelLoading(true);
      const fuelRes = await api.fuelLog.getAll();
      setFuelLogs(fuelRes.data || []);
    } catch (error) {
      console.error('Error loading fuel logs:', error);
      onMessage?.({
        title: t('fuel.messages.errorTitle'),
        message: t('fuel.errors.loadFailed'),
        type: 'error'
      });
    } finally {
      setFuelLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open creation form for a specific vehicle (from Vehicles action menu)
  useEffect(() => {
    if (createForVehicleId && !createForVehicleProcessed.current) {
      createForVehicleProcessed.current = true;
      setShowForm(true);
    }
  }, [createForVehicleId]);

  // Filter logs by search
  const filteredLogs = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return fuelLogs;
    const query = searchQuery.toLowerCase();
    return fuelLogs.filter(log => {
      const vehicle = vehicles.find(v => v.id === log.vehicle_id);
      const driver = drivers.find(d => d.id === log.driver_id);
      return (
        log.fuel_date?.toLowerCase().includes(query) ||
        log.fuel_type?.toLowerCase().includes(query) ||
        log.vendor?.toLowerCase().includes(query) ||
        vehicle?.license_plate?.toLowerCase().includes(query) ||
        vehicle?.make?.toLowerCase().includes(query) ||
        vehicle?.model?.toLowerCase().includes(query) ||
        driver?.first_name?.toLowerCase().includes(query) ||
        driver?.last_name?.toLowerCase().includes(query)
      );
    });
  }, [fuelLogs, vehicles, drivers, searchQuery]);

  // Group logs by current month vs history (previous months)
  // Les sous-totaux sont CONVERTIS dans la devise du périmètre (un plein polonais
  // est en PLN) — jamais additionnés bruts.
  const groupedLogs = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const groups = {
      current: {
        icon: Zap,
        logs: [],
        totalLiters: 0,
        totalCost: 0,
        costComplete: true
      },
      history: {
        icon: Archive,
        months: {},
        totalLogs: 0,
        totalCost: 0,
        costComplete: true
      }
    };

    // Sort logs by date descending
    const sortedLogs = [...filteredLogs].sort((a, b) =>
      new Date(b.fuel_date) - new Date(a.fuel_date)
    );

    sortedLogs.forEach(log => {
      const logDate = new Date(log.fuel_date);
      const logMonth = logDate.getMonth();
      const logYear = logDate.getFullYear();

      const cost = toScope(log.total_cost, currencyOf(log));

      if (logMonth === currentMonth && logYear === currentYear) {
        // Current month
        groups.current.logs.push(log);
        groups.current.totalLiters += parseFloat(log.liters) || 0;
        if (cost == null) groups.current.costComplete = false; else groups.current.totalCost += cost;
      } else {
        // History - group by month
        const monthKey = `${logYear}-${String(logMonth + 1).padStart(2, '0')}`;

        if (!groups.history.months[monthKey]) {
          groups.history.months[monthKey] = {
            key: monthKey,
            // Le libellé du mois est formaté au rendu (locale de l'UI)
            date: new Date(logYear, logMonth, 1),
            logs: [],
            totalLiters: 0,
            totalCost: 0,
            costComplete: true
          };
        }

        groups.history.months[monthKey].logs.push(log);
        groups.history.months[monthKey].totalLiters += parseFloat(log.liters) || 0;
        groups.history.totalLogs++;
        if (cost == null) {
          groups.history.months[monthKey].costComplete = false;
          groups.history.costComplete = false;
        } else {
          groups.history.months[monthKey].totalCost += cost;
          groups.history.totalCost += cost;
        }
      }
    });

    // Convert months to sorted array
    groups.history.monthsList = Object.values(groups.history.months)
      .sort((a, b) => b.key.localeCompare(a.key));

    return groups;
  }, [filteredLogs, toScope, currencyOf]);

  // Calculate stats
  // Coûts consolidés dans la devise du périmètre (conversion via fx_rates).
  const stats = useMemo(() => {
    const totalLiters = fuelLogs.reduce((sum, log) => sum + (parseFloat(log.liters) || 0), 0);
    const cost = sumScope(fuelLogs, log => log.total_cost);
    return {
      totalLogs: fuelLogs.length,
      totalLiters,
      cost,
      avgPricePerLiter: cost.complete && totalLiters > 0 ? cost.value / totalLiters : null,
      thisMonth: groupedLogs.current.totalCost,
      thisMonthComplete: groupedLogs.current.costComplete !== false
    };
  }, [fuelLogs, groupedLogs, sumScope]);

  const handleFormSuccess = () => {
    loadData();
    setShowForm(false);
    // Invalidate resource alerts so vehicle table shows updated fuel levels
    queryClient.invalidateQueries({ queryKey: ['resourceAlertsSummary'] });
    // Return to origin view (e.g. vehicles) after successful submission
    if (returnTo && onNavigate) {
      onNavigate(returnTo);
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm(t('fuel.messages.confirmDelete'))) return;

    try {
      await api.fuelLog.delete(logId);
      onMessage?.({
        title: t('fuel.messages.successTitle'),
        message: t('fuel.messages.deleted'),
        type: 'success'
      });
      loadData();
    } catch (error) {
      onMessage?.({
        title: t('fuel.messages.errorTitle'),
        message: t('fuel.errors.deleteFailed'),
        type: 'error'
      });
    }
  };

  // Get vehicle info
  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle
      ? `${vehicle.make} ${vehicle.model} - ${vehicle.license_plate}`
      : t('fuel.list.vehicleFallback', { id: vehicleId });
  };

  // Get driver info
  const getDriverInfo = (driverId) => {
    if (!driverId) return null;
    const driver = drivers.find(d => d.id === driverId);
    return driver ? `${driver.first_name} ${driver.last_name}` : null;
  };

  // Render a fuel log card
  const renderFuelLogCard = (log) => {
    const typeCode = FUEL_TYPE_CONFIG[log.fuel_type] ? log.fuel_type : 'other';
    const fuelConfig = FUEL_TYPE_CONFIG[typeCode];
    const pricePerLiter = log.liters > 0 ? log.total_cost / log.liters : 0;

    return (
      <div
        key={log.id}
        className="bg-white border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-gray-900">
                {log.fuel_date}
              </h4>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${fuelConfig.color}`}>
                <span>{fuelConfig.icon}</span>
                {t(`fuel.types.${typeCode}`)}
              </span>
            </div>
            <div className="text-sm text-gray-600 mb-2">
              {getVehicleInfo(log.vehicle_id)}
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="font-semibold text-green-600">
                {t('fuel.list.liters', { value: fmt.number(log.liters, { decimals: 2 }) })}
              </span>
              <span className="font-bold text-gray-900">
                {money(log.total_cost, log)}
              </span>
              <span>{t('fuel.list.pricePerLiter', { price: fmt.number(pricePerLiter, { decimals: 3 }), symbol: symbolOf(log) })}</span>
              {log.odometer && (
                <span>{t('fuel.list.odometer', { value: fmt.number(log.odometer) })}</span>
              )}
              {getDriverInfo(log.driver_id) && (
                <span>• {getDriverInfo(log.driver_id)}</span>
              )}
            </div>
            {log.vendor && (
              <div className="text-xs text-gray-400 mt-1">
                {t('fuel.list.station', { name: log.vendor })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => setSelectedLog(log)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
              title={t('fuel.list.viewDetails')}
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => !isReadOnly && handleDelete(log.id)}
              disabled={isReadOnly}
              className={`p-2 rounded-lg ${isReadOnly ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
              title={isReadOnly ? t('common.actions.readOnlyTooltip') : t('common.actions.delete')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && mainTab === 'logs') return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Main Tab Toggle */}
      <div className="flex gap-2">
        {showLogsTab && (
          <button
            onClick={() => setMainTab('logs')}
            className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              mainTab === 'logs'
                ? 'bg-green-500 text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Fuel className="w-5 h-5" />
            {t('fuel.tabs.logs')}
          </button>
        )}
        {showCardsTab && (
          <button
            onClick={() => setMainTab('cards')}
            className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              mainTab === 'cards'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            <CreditCard className="w-5 h-5" />
            {t('fuel.tabs.cards')}
          </button>
        )}
      </div>

      {/* Cards Tab */}
      {mainTab === 'cards' && <FuelCardView onMessage={onMessage} />}

      {/* Logs Tab */}
      {mainTab === 'logs' && <>
      {/* Form Modal */}
      <FuelFormModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={handleFormSuccess}
        onMessage={onMessage}
        vehicles={vehicles}
        drivers={drivers}
        defaultVehicleId={createForVehicleId}
        defaultFuelType={defaultFuelType}
      />

      {/* Detail Modal */}
      {selectedLog && (
        <FuelDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onUpdate={loadData}
          onMessage={onMessage}
          vehicles={vehicles}
          drivers={drivers}
          readOnly={isReadOnly}
        />
      )}

      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Fuel className="w-6 h-6 text-green-600" />
              {t('fuel.list.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{stats.totalLogs}</span> {t('fuel.list.logsUnit', { count: stats.totalLogs })}
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{t('fuel.list.liters', { value: fmt.number(stats.totalLiters, { decimals: 1 }) })}</span>
              <span className="text-gray-300">|</span>
              <span className="text-blue-600">{stats.cost.label}</span>
              <span className="text-gray-300">|</span>
              <span className="text-orange-600">{stats.avgPricePerLiter != null
                ? t('fuel.list.avgPricePerLiter', { price: fmt.number(stats.avgPricePerLiter, { decimals: 3 }), symbol: scopeSymbol })
                : t('fuel.list.avgPricePerLiterEmpty')}</span>
              <span className="text-gray-300">|</span>
              <span className="text-purple-600">{t('fuel.list.thisMonth', { amount: stats.thisMonthComplete ? moneyScope(stats.thisMonth) : '—' })}</span>
            </p>
          </div>
          {!isReadOnly && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('fuel.list.newLog')}
            </button>
          )}
        </div>

        {/* Refresh */}
        <div className="flex gap-4 pt-4 border-t border-gray-200 justify-end">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Accordion Groups */}
      <div className="space-y-3">
        {/* Current Month */}
        <div
          className={`border rounded-lg overflow-hidden transition-all ${
            isExpanded('group:current') ? 'border-green-300 shadow-sm' : 'border-gray-200'
          }`}
        >
          {/* Group Header */}
          <button
            onClick={() => toggleGroup('current')}
            className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
              isExpanded('group:current')
                ? 'bg-green-50 hover:bg-green-100'
                : 'bg-white hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-3">
              {isExpanded('group:current') ? (
                <FolderOpen className="w-5 h-5 text-green-600" />
              ) : (
                <Folder className="w-5 h-5 text-gray-400" />
              )}
              <Zap className="w-5 h-5 text-green-500" />
              <span className={`text-lg font-semibold ${
                isExpanded('group:current') ? 'text-green-900' : 'text-gray-900'
              }`}>
                {t('fuel.list.groupCurrent')}
              </span>
              {groupedLogs.current.logs.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  {t('fuel.list.badgeActive')}
                </span>
              )}
            </div>

            <div className="flex items-center gap-4">
              {!isExpanded('group:current') && (
                <span className="text-sm text-gray-500">
                  {t('fuel.list.currentSummary', {
                    logs: t('fuel.list.logsCount', { count: groupedLogs.current.logs.length }),
                    amount: groupedLogs.current.costComplete ? moneyScope(groupedLogs.current.totalCost) : '—'
                  })}
                </span>
              )}
              <span className={`px-2.5 py-1 text-sm font-medium rounded-full ${
                isExpanded('group:current') ? 'bg-green-200 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {groupedLogs.current.logs.length}
              </span>
              {isExpanded('group:current') ? (
                <ChevronDown className="w-5 h-5 text-green-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </button>

          {/* Current Month Content */}
          {isExpanded('group:current') && (
            <div className="border-t border-green-200 bg-gray-50 p-4">
              {groupedLogs.current.logs.length > 0 ? (
                <div className="space-y-2">
                  {groupedLogs.current.logs.map(log => renderFuelLogCard(log))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <Fuel className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('fuel.list.emptyCurrent')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* History */}
        <div
          className={`border rounded-lg overflow-hidden transition-all ${
            isExpanded('group:history') ? 'border-blue-300 shadow-sm' : 'border-gray-200'
          }`}
        >
          {/* Group Header */}
          <button
            onClick={() => toggleGroup('history')}
            className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
              isExpanded('group:history')
                ? 'bg-blue-50 hover:bg-blue-100'
                : 'bg-white hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-3">
              {isExpanded('group:history') ? (
                <FolderOpen className="w-5 h-5 text-blue-600" />
              ) : (
                <Folder className="w-5 h-5 text-gray-400" />
              )}
              <Archive className="w-5 h-5 text-blue-500" />
              <span className={`text-lg font-semibold ${
                isExpanded('group:history') ? 'text-blue-900' : 'text-gray-900'
              }`}>
                {t('fuel.list.groupHistory')}
              </span>
            </div>

            <div className="flex items-center gap-4">
              {!isExpanded('group:history') && (
                <span className="text-sm text-gray-500">
                  {t('fuel.list.historySummary', {
                    logs: t('fuel.list.logsCount', { count: groupedLogs.history.totalLogs }),
                    months: t('fuel.list.monthsCount', { count: groupedLogs.history.monthsList.length })
                  })}
                </span>
              )}
              <span className={`px-2.5 py-1 text-sm font-medium rounded-full ${
                isExpanded('group:history') ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {groupedLogs.history.totalLogs}
              </span>
              {isExpanded('group:history') ? (
                <ChevronDown className="w-5 h-5 text-blue-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </button>

          {/* History Content - Month Accordions */}
          {isExpanded('group:history') && (
            <div className="border-t border-blue-200 bg-gray-50">
              {groupedLogs.history.monthsList.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {groupedLogs.history.monthsList.map((monthData) => {
                    const isMonthExpanded = isExpanded(`month:${monthData.key}`);

                    return (
                      <div key={monthData.key} className="bg-white">
                        {/* Month Header */}
                        <button
                          onClick={() => toggleMonth(monthData.key)}
                          className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                            isMonthExpanded
                              ? 'bg-gray-50 border-l-4 border-blue-500'
                              : 'hover:bg-gray-50 border-l-4 border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {isMonthExpanded ? (
                              <ChevronDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className={`font-medium capitalize ${
                              isMonthExpanded ? 'text-blue-900' : 'text-gray-700'
                            }`}>
                              {fmt.date(monthData.date, 'monthYear')}
                            </span>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                              {t('fuel.list.monthSummary', {
                                liters: fmt.number(monthData.totalLiters, { decimals: 1 }),
                                amount: monthData.costComplete ? moneyScope(monthData.totalCost) : '—'
                              })}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              isMonthExpanded
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {monthData.logs.length}
                            </span>
                          </div>
                        </button>

                        {/* Month Content */}
                        {isMonthExpanded && (
                          <div className="px-4 py-3 space-y-2 bg-gray-50 border-l-4 border-blue-500">
                            {monthData.logs.map(log => renderFuelLogCard(log))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('fuel.list.emptyHistory')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Global Empty State */}
      {fuelLogs.length === 0 && (
        <div className="bg-white p-12 rounded-lg shadow-md text-center">
          <Fuel className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('fuel.list.emptyTitle')}</h3>
          <p className="text-gray-600 mb-4">
            {t('fuel.list.emptyHint')}
          </p>
          {!isReadOnly && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('fuel.list.newLog')}
            </button>
          )}
        </div>
      )}
      </>}
    </div>
  );
}

export default FuelView;
